/**
 * AdminUsers.js
 * Unified User Management Hub
 * Manage company users and admin users from one page (the old supplier
 * portal's user tab left with Lane 1410 W6).
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { Modal } from '../components/SharedComponents';
import { API_BASE } from '../config';
import colors from '../utils/adminColors';

function AdminUsers() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('admin');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Data
  const [companyUsers, setCompanyUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Filters
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting
  const [sortColumn, setSortColumn] = useState('full_name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [createForm, setCreateForm] = useState({});
  const [inviteResult, setInviteResult] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Action dropdown
  const [openActionMenu, setOpenActionMenu] = useState(null);

  const headers = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  });

  // Fetch on mount and tab change
  useEffect(() => { fetchCompanies(); }, []);
  useEffect(() => { fetchData(); }, [activeTab, filterCompanyId]);

  // Close action menu on click outside
  useEffect(() => {
    const handleClick = () => setOpenActionMenu(null);
    if (openActionMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [openActionMenu]);

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/companies-list`, { headers: headers() });
      const data = await res.json();
      if (data.success) setCompanies(data.companies || []);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'company') {
        const res = await fetch(`${API_BASE}/api/admin/users`, { headers: headers() });
        const data = await res.json();
        if (data.success) {
          let users = data.users || [];
          if (filterCompanyId) users = users.filter(u => String(u.company_id) === String(filterCompanyId));
          if (searchTerm) {
            const term = searchTerm.toLowerCase();
            users = users.filter(u => (u.full_name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term));
          }
          setCompanyUsers(users);
        }
      } else if (activeTab === 'admin') {
        const res = await fetch(`${API_BASE}/api/admin/users/admins`, { headers: headers() });
        const data = await res.json();
        if (data.success) setAdminUsers(data.users || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  // --- Actions ---

  const toggleStatus = async (userType, userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const urlMap = {
      company: `${API_BASE}/api/admin/users/company/${userId}/status`,
      admin: `${API_BASE}/api/admin/users/admins/${userId}/status`,
    };
    try {
      const res = await fetch(urlMap[userType], {
        method: 'PATCH', headers: headers(),
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        showMsg('success', `User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchData();
      } else {
        showMsg('error', data.detail || 'Failed to update status');
      }
    } catch (e) { showMsg('error', 'Failed to update status'); }
  };

  // Lane 1426: the server makes a random one-time password and returns it once
  // (new_password); the screen shows it in the "won't be shown again" modal.
  const resetPassword = async (userType, userId) => {
    const urlMap = {
      company: `${API_BASE}/api/admin/users/company/${userId}/reset-password`,
      admin: `${API_BASE}/api/admin/users/admins/${userId}/reset-password`,
    };
    try {
      const res = await fetch(urlMap[userType], {
        method: 'POST', headers: headers(),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success && data.new_password) {
        setResetResult({ email: data.email, password: data.new_password });
      } else {
        showMsg('error', data.detail || 'Failed to reset password');
      }
    } catch (e) { showMsg('error', 'Failed to reset password'); }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/deactivate`, {
        method: 'PATCH', headers: headers(),
      });
      const data = await res.json();
      if (data.success) {
        showMsg('success', 'User deactivated');
        fetchData();
      } else {
        showMsg('error', data.detail || 'Failed to deactivate user');
      }
    } catch (e) { showMsg('error', 'Failed to deactivate user'); }
    setConfirmDeactivate(null);
  };

  const handleDeleteUser = async (userType, userId) => {
    const urlMap = {
      company: `${API_BASE}/api/admin/users/company/${userId}`,
      admin: `${API_BASE}/api/admin/users/admins/${userId}`,
    };
    try {
      const res = await fetch(urlMap[userType], {
        method: 'DELETE', headers: headers(),
      });
      const data = await res.json();
      if (data.success) {
        showMsg('success', data.message || 'User permanently deleted');
        fetchData();
      } else {
        showMsg('error', data.detail || 'Failed to delete user');
      }
    } catch (e) { showMsg('error', 'Failed to delete user'); }
    setConfirmDelete(null);
  };

  const handleCreate = async () => {
    let url, body;
    if (activeTab === 'company') {
      // Use the invite endpoint — auto-generates temp password
      if (!createForm.company_id) {
        showMsg('error', 'Please select a company');
        return;
      }
      url = `${API_BASE}/api/admin/users/invite`;
      body = {
        name: createForm.full_name || '',
        email: createForm.email || '',
        company_id: parseInt(createForm.company_id),
      };
    } else {
      url = `${API_BASE}/api/admin/users/admins`;
      body = {
        email: createForm.email || '',
        password: createForm.password || '',
        full_name: createForm.full_name || '',
        role: createForm.role || 'admin',
      };
    }

    try {
      const res = await fetch(url, {
        method: 'POST', headers: headers(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && (data.success || data.user)) {
        if (activeTab === 'company' && data.temporary_password) {
          // Show the temp password for the admin to copy
          setInviteResult({ email: createForm.email, password: data.temporary_password });
        } else {
          showMsg('success', 'User created successfully');
        }
        setShowCreateModal(false);
        setCreateForm({});
        fetchData();
      } else {
        showMsg('error', data.detail || 'Failed to create user');
      }
    } catch (e) { showMsg('error', 'Failed to create user'); }
  };

  // --- Sorting ---

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const currentUsers = useMemo(() => {
    if (activeTab === 'company') return sortData(companyUsers);
    return sortData(adminUsers);
  }, [activeTab, companyUsers, adminUsers, sortColumn, sortDirection]);

  // --- Helpers ---

  const formatDateTime = (d) => {
    if (!d) return 'Never';
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  const StatusBadge = ({ status }) => {
    const isActive = status === 'active';
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        backgroundColor: isActive ? `${colors.success}15` : `${colors.danger}15`,
        color: isActive ? colors.success : colors.danger,
      }}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  // --- Sortable Header ---

  const SortableHeader = ({ column, label, style = {} }) => {
    const isActive = sortColumn === column;
    return (
      <th
        onClick={() => handleSort(column)}
        style={{
          ...thStyle,
          ...style,
          cursor: 'pointer',
          userSelect: 'none',
          color: isActive ? colors.accent : colors.textMuted,
          transition: 'color 0.15s ease',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {label}
          {isActive && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              {sortDirection === 'asc'
                ? <path d="M6 2.5L6 9.5M6 2.5L3 5.5M6 2.5L9 5.5" />
                : <path d="M6 9.5L6 2.5M6 9.5L3 6.5M6 9.5L9 6.5" />
              }
            </svg>
          )}
        </span>
      </th>
    );
  };

  // --- Action Dropdown ---

  const ActionDropdown = ({ userId, userType, status, user, onToggleStatus }) => {
    const isOpen = openActionMenu === `${userType}-${userId}`;
    const menuId = `${userType}-${userId}`;
    const btnRef = useRef(null);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

    const openMenu = useCallback((e) => {
      e.stopPropagation();
      if (isOpen) { setOpenActionMenu(null); return; }
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        const dropdownHeight = 160; // approximate menu height
        const spaceBelow = window.innerHeight - rect.bottom;
        const goUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
        setMenuPos({
          top: goUp ? rect.top - dropdownHeight : rect.bottom + 4,
          left: Math.max(8, rect.right - 200),
        });
      }
      setOpenActionMenu(menuId);
    }, [isOpen, menuId]);

    return (
      <div style={{ display: 'inline-block' }}>
        <button
          ref={btnRef}
          onClick={openMenu}
          style={{
            padding: '4px 10px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)',
            color: colors.textMuted, backgroundColor: colors.lightGray,
            border: `1px solid ${colors.mediumGray}`, borderRadius: '6px',
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Actions
        </button>
        {isOpen && (
          <div
            style={{
              position: 'fixed',
              top: menuPos.top, left: menuPos.left,
              backgroundColor: colors.white, borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)', border: `1px solid ${colors.mediumGray}`,
              zIndex: 99999, minWidth: '200px',
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onToggleStatus) { onToggleStatus(); } else { toggleStatus(userType, userId, status); }
                setOpenActionMenu(null);
              }}
              style={menuItemStyle}
            >
              <span style={{ color: status === 'active' ? colors.danger : colors.success }}>
                {status === 'active' ? 'Deactivate' : 'Activate'}
              </span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); resetPassword(userType, userId); setOpenActionMenu(null); }}
              style={menuItemStyle}
            >
              <span style={{ color: colors.warning }}>Reset Password</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDelete({ userType, userId, user });
                setOpenActionMenu(null);
              }}
              style={{ ...menuItemStyle, borderTop: `1px solid ${colors.mediumGray}` }}
            >
              <span style={{ color: colors.danger, fontWeight: 'var(--font-weight-semibold)' }}>Delete Permanently</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  // --- Tab content ---

  const tabs = [
    { id: 'admin', label: 'Admin Users', count: adminUsers.length },
  ];

  const thStyle = {
    padding: '8px 12px',
    textAlign: 'left',
    fontWeight: 'var(--font-weight-medium)',
    fontSize: 'var(--font-size-xs)',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 'var(--letter-spacing-wide)',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #E2E5EA',
    backgroundColor: 'transparent',
  };

  const tdStyle = {
    padding: '10px 12px',
    fontSize: 'var(--font-size-sm)',
    color: colors.darkText,
  };

  const truncStyle = {
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const menuItemStyle = {
    display: 'block', width: '100%', padding: '8px 16px',
    background: 'none', border: 'none', textAlign: 'left',
    cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)',
  };

  const getUserId = (u) => {
    if (activeTab === 'company') return u.user_id;
    return u.admin_id;
  };

  return (
    <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText, margin: '0 0 6px 0' }}>
          User Management
        </h1>
        <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', margin: 0 }}>
          Manage all users across the company and admin portals
        </p>
      </div>

      {/* Message toast */}
      {message && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          padding: '12px 20px', borderRadius: '8px',
          backgroundColor: message.type === 'success' ? colors.success : colors.danger,
          color: colors.white, fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)',
          boxShadow: 'var(--shadow-md)',
        }}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: '2px', marginBottom: '24px',
        backgroundColor: colors.mediumGray, borderRadius: '10px', padding: '3px',
      }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchTerm(''); setFilterCompanyId(''); setSortColumn('full_name'); setSortDirection('asc'); }}
            style={{
              flex: 1, padding: '8px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontSize: 'var(--font-size-base)', fontWeight: activeTab === tab.id ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
              backgroundColor: activeTab === tab.id ? colors.white : 'transparent',
              color: activeTab === tab.id ? colors.darkText : colors.textMuted,
              boxShadow: activeTab === tab.id ? 'var(--shadow-sm)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}>
            {tab.label}
            <span style={{
              padding: '1px 8px', borderRadius: '100px', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)',
              backgroundColor: activeTab === tab.id ? `${colors.adminPurple}15` : `${colors.textMuted}15`,
              color: activeTab === tab.id ? colors.adminPurple : colors.textMuted,
            }}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap',
      }}>
        {activeTab !== 'admin' && (
          <select
            value={filterCompanyId}
            onChange={(e) => setFilterCompanyId(e.target.value)}
            style={{
              padding: '12px 16px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px',
              fontSize: 'var(--font-size-sm)', backgroundColor: colors.white, minWidth: '180px',
            }}>
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.company_id} value={c.company_id}>
                {c.company_name} ({c.company_code})
              </option>
            ))}
          </select>
        )}
        <input
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchData()}
          style={{
            padding: '12px 16px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px',
            fontSize: 'var(--font-size-sm)', flex: 1, minWidth: '200px',
          }}
        />
        <button onClick={fetchData} style={{
          padding: '8px 16px', backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`,
          borderRadius: '6px', fontSize: 'var(--font-size-sm)', cursor: 'pointer', fontWeight: 'var(--font-weight-medium)',
        }}>Search</button>
        <button onClick={() => { setCreateForm({}); setShowCreateModal(true); }} style={{
          padding: '8px 16px', backgroundColor: colors.adminPurple, color: colors.white,
          border: 'none', borderRadius: '6px', fontSize: 'var(--font-size-sm)', cursor: 'pointer', fontWeight: 'var(--font-weight-semibold)',
        }}>
          + {activeTab === 'company' ? 'Invite' : 'Create'} {activeTab === 'company' ? 'Company' : 'Admin'} User
        </button>
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: colors.white, borderRadius: '12px',
        boxShadow: 'var(--shadow-sm)', overflow: 'visible',
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>Loading...</div>
        ) : (
          <div>
            <table className="standard-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                {activeTab === 'company' && <>
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                </>}
                {activeTab === 'admin' && <>
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '9%' }} />
                </>}
              </colgroup>
              <thead>
                <tr>
                  <SortableHeader column="full_name" label="Name" />
                  <SortableHeader column="email" label="Email" />
                  {activeTab === 'company' && <SortableHeader column="company_name" label="Company" />}
                  {activeTab === 'company' && <SortableHeader column="role_title" label="Role" />}
                  {activeTab === 'company' && <th style={thStyle}>Active</th>}
                  {activeTab === 'admin' && <SortableHeader column="role" label="Role" />}
                  {activeTab !== 'company' && <SortableHeader column="status" label="Status" />}
                  <SortableHeader column="last_login" label="Last Login" />
                  <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'company' ? 7 : 6}
                      style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>
                      No {activeTab} users found.
                    </td>
                  </tr>
                ) : currentUsers.map((u, rowIdx) => {
                  const userId = getUserId(u);
                  return (
                    <tr key={userId} style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                      <td style={{ ...tdStyle, fontWeight: 'var(--font-weight-medium)', ...truncStyle }}>{u.full_name || 'Not available'}</td>
                      <td style={{ ...tdStyle, ...truncStyle }} title={u.email}>{u.email}</td>
                      {activeTab === 'company' && (
                        <td style={{ ...tdStyle, ...truncStyle }}>{u.company_name}</td>
                      )}
                      {activeTab === 'company' && (
                        <td style={{ ...tdStyle, ...truncStyle }}>{u.role_title || 'Not available'}</td>
                      )}
                      {activeTab === 'company' && (
                        <td style={tdStyle}>
                          <span style={{
                            padding: '4px 10px', borderRadius: '12px', fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-semibold)',
                            backgroundColor: u.is_active !== false ? `${colors.success}15` : `${colors.danger}15`,
                            color: u.is_active !== false ? colors.success : colors.danger,
                          }}>
                            {u.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                      {activeTab === 'admin' && (
                        <td style={tdStyle}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '100px', fontSize: 'var(--font-size-xs)',
                            backgroundColor: u.role === 'super_admin'
                              ? `${colors.danger}10` : `${colors.adminPurple}10`,
                            color: u.role === 'super_admin'
                              ? colors.danger : colors.adminPurple,
                            fontWeight: 'var(--font-weight-medium)', textTransform: 'capitalize',
                          }}>
                            {u.role === 'super_admin' ? 'Super Admin' : u.role}
                          </span>
                        </td>
                      )}
                      {activeTab !== 'company' && (
                        <td style={tdStyle}><StatusBadge status={u.status} /></td>
                      )}
                      <td style={{ ...tdStyle, color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
                        {formatDateTime(u.last_login)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center', position: 'relative', overflow: 'visible' }}>
                        <ActionDropdown
                          userId={userId}
                          userType={activeTab}
                          status={activeTab === 'company' ? ((u.is_active === false || u.status === 'inactive') ? 'inactive' : 'active') : u.status}
                          user={u}
                          openUpward={currentUsers.length > 2 && rowIdx >= currentUsers.length - 2}
                          onToggleStatus={activeTab === 'company' ? () => {
                            if (u.is_active !== false) {
                              setConfirmDeactivate(u);
                            } else {
                              toggleStatus('company', u.user_id, 'inactive');
                            }
                          } : undefined}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== CREATE USER MODAL ===== */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title={activeTab === 'company' ? 'Invite Company User' : 'Create Admin User'} size="medium">

            {activeTab === 'company' && (
              <>
                <FormField label="Company *">
                  <select value={createForm.company_id || ''} onChange={(e) => setCreateForm({ ...createForm, company_id: e.target.value })} style={inputStyle}>
                    <option value="">Select company...</option>
                    {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
                  </select>
                </FormField>
                <div style={{ padding: '10px 14px', backgroundColor: '#F0F1F3', borderRadius: '8px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '14px' }}>
                  A temporary password will be generated automatically. You will see it after creating the user.
                </div>
              </>
            )}

            {activeTab === 'admin' && (
              <FormField label="Role">
                <select value={createForm.role || 'admin'} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })} style={inputStyle}>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </FormField>
            )}

            <FormField label="Full Name *">
              <input type="text" value={createForm.full_name || ''}
                onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                placeholder="John Smith" style={inputStyle} />
            </FormField>
            <FormField label="Email *">
              <input type="email" value={createForm.email || ''}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@example.com" style={inputStyle} />
            </FormField>
            {activeTab !== 'company' && (
              <FormField label="Password *">
                <input type="text" value={createForm.password || ''}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Min 8 characters" style={inputStyle} />
                <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginTop: '4px' }}>
                  Shown in plain text so you can share with the user
                </div>
              </FormField>
            )}

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateModal(false)} style={{
                padding: '8px 16px', backgroundColor: colors.lightGray, border: `1px solid ${colors.mediumGray}`,
                borderRadius: '6px', cursor: 'pointer', fontSize: 'var(--font-size-base)',
              }}>Cancel</button>
              <button onClick={handleCreate} style={{
                padding: '8px 16px', backgroundColor: colors.adminPurple, color: colors.white,
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)',
              }}>Create User</button>
            </div>
      </Modal>

      {/* ===== PASSWORD RESET RESULT MODAL ===== */}
      <Modal isOpen={!!resetResult} onClose={() => setResetResult(null)} title="Password Reset" size="small">
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px', backgroundColor: colors.lightGray,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.adminPurple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
              </svg>
            </div>
            <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', marginBottom: '20px' }}>
              New credentials for <strong>{resetResult?.email}</strong>
            </p>
            <div style={{
              backgroundColor: colors.lightGray, borderRadius: '8px', padding: '16px',
              fontFamily: 'monospace', fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText,
              letterSpacing: '1px', marginBottom: '20px', userSelect: 'all',
            }}>
              {resetResult?.password}
            </div>
            <p style={{ fontSize: 'var(--font-size-sm)', color: colors.warning, marginBottom: '20px' }}>
              Copy this password now -- it won't be shown again
            </p>
            <button onClick={() => {
              navigator.clipboard?.writeText(resetResult?.password);
              setResetResult(null);
              showMsg('success', 'Password copied to clipboard');
            }} style={{
              padding: '8px 16px', backgroundColor: colors.adminPurple, color: colors.white,
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)',
            }}>Copy & Close</button>
          </div>
      </Modal>

      {/* ===== INVITE RESULT MODAL (temp password) ===== */}
      <Modal isOpen={!!inviteResult} onClose={() => setInviteResult(null)} title="User Invited" size="small">
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px', backgroundColor: `${colors.success}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', marginBottom: '8px' }}>
              Account created for <strong>{inviteResult?.email}</strong>
            </p>
            <p style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginBottom: '16px' }}>
              Share this temporary password with the user:
            </p>
            <div style={{
              backgroundColor: colors.lightGray, borderRadius: '8px', padding: '16px',
              fontFamily: 'monospace', fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText,
              letterSpacing: '1px', marginBottom: '20px', userSelect: 'all',
            }}>
              {inviteResult?.password}
            </div>
            <p style={{ fontSize: 'var(--font-size-sm)', color: colors.warning, marginBottom: '20px' }}>
              Copy this password now -- it won't be shown again
            </p>
            <button onClick={() => {
              navigator.clipboard?.writeText(inviteResult?.password);
              setInviteResult(null);
              showMsg('success', 'Password copied to clipboard');
            }} style={{
              padding: '8px 16px', backgroundColor: colors.adminPurple, color: colors.white,
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)',
            }}>Copy & Close</button>
          </div>
      </Modal>

      {/* ===== DEACTIVATE CONFIRMATION MODAL ===== */}
      <Modal isOpen={!!confirmDeactivate} onClose={() => setConfirmDeactivate(null)} title="Confirm Deactivation" size="small">
          <div>
            <p style={{ color: colors.darkText, fontSize: 'var(--font-size-base)', marginBottom: '8px' }}>
              Are you sure you want to deactivate <strong>{confirmDeactivate?.full_name}</strong>?
            </p>
            <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)', marginBottom: '24px' }}>
              This user ({confirmDeactivate?.email}) will no longer be able to log in.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDeactivate(null)} style={{
                padding: '8px 16px', backgroundColor: colors.lightGray, border: `1px solid ${colors.mediumGray}`,
                borderRadius: '6px', cursor: 'pointer', fontSize: 'var(--font-size-base)',
              }}>Cancel</button>
              <button onClick={() => handleDeactivateUser(confirmDeactivate?.user_id)} style={{
                padding: '8px 16px', backgroundColor: colors.danger, color: colors.white,
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)',
              }}>Deactivate User</button>
            </div>
          </div>
      </Modal>

      {/* ===== DELETE CONFIRMATION MODAL ===== */}
      <Modal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Permanently Delete User" size="small">
          <div>
            <p style={{ color: colors.danger, fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', marginBottom: '8px' }}>
              This action cannot be undone.
            </p>
            <p style={{ color: colors.darkText, fontSize: 'var(--font-size-base)', marginBottom: '8px' }}>
              Permanently delete <strong>{confirmDelete?.user?.full_name || confirmDelete?.user?.email}</strong>?
            </p>
            <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)', marginBottom: '24px' }}>
              {confirmDelete?.userType === 'company'
                ? 'This will also delete their interview sessions, intelligence entries, and coverage claims. Created records (RFPs, documents) will be preserved with authorship cleared.'
                : 'This user and their login credentials will be permanently removed.'}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{
                padding: '8px 16px', backgroundColor: colors.lightGray, border: `1px solid ${colors.mediumGray}`,
                borderRadius: '6px', cursor: 'pointer', fontSize: 'var(--font-size-base)',
              }}>Cancel</button>
              <button onClick={() => handleDeleteUser(confirmDelete?.userType, confirmDelete?.userId)} style={{
                padding: '8px 16px', backgroundColor: colors.danger, color: colors.white,
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)',
              }}>Delete Permanently</button>
            </div>
          </div>
      </Modal>

    </div>
  );
}

// --- Reusable Components ---

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)', color: '#1A2B3C' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '12px 16px', border: '1px solid #E1E5EB',
  borderRadius: '8px', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box',
};

export default AdminUsers;
